import type {
  ContentBlock,
  FlashCard,
  FlippingCardItem,
  QuizTabStatus,
  TabbedMessageContent,
} from "../../../../types";
import { resolveTranscriptData } from "../../../../utils/transcription";
import { fixMalformedUnicodeText } from "../../../../utils/textSanitize";

export function blockHasContent(b: ContentBlock): boolean {
  switch (b.type) {
    case "text":
      return !!b.content?.trim();
    case "quiz":
      return !!b.text?.trim() || !!b.buttons?.length;
    case "buttons":
      return !!b.text?.trim() || !!b.buttons?.length;
    case "image_collection":
      return !!b.image_collection?.length;
    case "flash_card":
      return !!b.flash_card?.length;
    case "flash_card_image_collection":
      return !!b.image_collection?.length;
    case "flipping_card":
      return !!b.flipping_card?.length;
    case "image":
      return !!b.file_url?.trim();
    case "audio":
      return true;
    case "video":
      return !!b.video_url;
    case "pdf":
      return !!b.pdf_url;
    case "chart":
      return !!b.chart_name?.trim() && !!b.items?.length;
    case "feedback":
      return !!b.text?.trim() || b.score !== undefined;
    case "fill_in_blank":
      return !!b.template?.trim() && !!b.options?.length;
    case "ordering":
      return !!b.options?.length && (b.slot_count ?? 0) > 0;
    case "matching":
      return !!b.left?.length && !!b.right?.length;
    case "ordered_response":
      return !!b.options?.length && (b.slot_count ?? 0) > 0;
    default:
      return false;
  }
}

export function tabHasContent(tab: TabbedMessageContent): boolean {
  if (tab.content_blocks?.length) {
    return tab.content_blocks.some(blockHasContent);
  }
  return !!(tab.content && tab.content.trim() !== "");
}

function toBlockArray(contentBlocks: unknown): unknown[] {
  if (!contentBlocks) return [];
  if (Array.isArray(contentBlocks)) return contentBlocks;
  if (typeof contentBlocks === "object" && contentBlocks !== null)
    return [contentBlocks];
  return [];
}

function normalizeFlashCard(raw: unknown): FlashCard[][] {
  if (Array.isArray(raw)) return raw as FlashCard[][];
  const obj = raw as Record<string, unknown> | null;
  if (!obj) return [];
  if (obj.dictionary && typeof obj.dictionary === "object") {
    const cards: FlashCard[] = [];
    for (const [key, val] of Object.entries(obj.dictionary)) {
      const v = val as Record<string, string> | null;
      if (!v) continue;
      const parts: string[] = [];
      if (v.question) parts.push(`**Question:** ${v.question}`);
      if (v.answer) parts.push(`**Answer:** ${v.answer}`);
      if (v.example) parts.push(`**Example:** ${v.example}`);
      cards.push({
        title: key,
        text: parts.join("\n\n"),
        subtitle: "",
        custom_background: null,
        title_step: "",
      });
    }
    return cards.length ? [cards] : [];
  }
  if (typeof obj.text === "string") {
    return [
      [
        {
          title: "",
          text: obj.text,
          subtitle: "",
          custom_background: null,
          title_step: "",
        },
      ],
    ];
  }
  return [];
}

function normalizeFlippingCard(raw: unknown): FlippingCardItem[] {
  if (!Array.isArray(raw)) return [];

  return raw
    .filter((item) => item && typeof item === "object")
    .map((item) => {
      const card = item as Record<string, unknown>;
      return {
        ...(typeof card.title === "string"
          ? { title: fixMalformedUnicodeText(card.title) }
          : {}),
        front_text: fixMalformedUnicodeText(String(card.front_text ?? "")),
        back_text: fixMalformedUnicodeText(String(card.back_text ?? "")),
      };
    });
}

function normalizeBlock(block: Record<string, unknown>): ContentBlock {
  const rawType = block.type;
  const type =
    typeof rawType === "string" ? rawType.trim().toLowerCase() : "";

  if (type === "audio") {
    const audio_url = (block.audio_url ?? block.file_url ?? "") as string;
    const title = typeof block.title === "string" ? block.title : undefined;
    const resolvedTranscript = resolveTranscriptData({
      transcription: block.transcription,
      transcriptionTimed: block.transcription_timed,
    });
    const transcription_timed: Extract<
      ContentBlock,
      { type: "audio" }
    >["transcription_timed"] = resolvedTranscript.transcriptionTimed;
    const transcription = resolvedTranscript.transcription;

    return {
      type: "audio",
      audio_url,
      ...(title !== undefined ? { title } : {}),
      ...(transcription !== undefined ? { transcription } : {}),
      ...(transcription_timed !== undefined ? { transcription_timed } : {}),
    };
  }
  if (type === "flash_card" && block.flash_card) {
    return {
      type: "flash_card",
      flash_card: normalizeFlashCard(block.flash_card),
    };
  }
  if (type === "flipping_card" && block.flipping_card) {
    return {
      type: "flipping_card",
      flipping_card: normalizeFlippingCard(block.flipping_card),
    };
  }
  if (
    type === "flash_card_image_collection" &&
    Array.isArray(block.image_collection)
  ) {
    return {
      type: "flash_card_image_collection",
      image_collection: block.image_collection.map(
        (img: Record<string, unknown>) => ({
          url: (img.url ?? "") as string,
          description: (img.description ?? "") as string,
        }),
      ),
    };
  }
  if (type === "feedback") {
    const text = (block.text ?? block.content ?? "") as string;
    const score = typeof block.score === "number" ? block.score : 0;
    return { type: "feedback", text, score };
  }
  if (type === "pdf") {
    const pdf_url = (block.pdf_url ?? block.file_url ?? "") as string;
    const title = typeof block.title === "string" ? block.title : undefined;
    return title !== undefined
      ? { type: "pdf", pdf_url, title }
      : { type: "pdf", pdf_url };
  }
  if (type === "image") {
    const file_url = (block.file_url ?? block.url ?? "") as string;
    const alt = typeof block.alt === "string" ? block.alt : undefined;
    return alt !== undefined
      ? { type: "image", file_url, alt }
      : { type: "image", file_url };
  }
  if (type === "fill_in_blank") {
    return normalizeFillInBlankBlock(block);
  }
  if (type === "ordering") {
    return normalizeOrderingBlock(block);
  }
  if (type === "matching") {
    return normalizeMatchingBlock(block);
  }
  if (type === "ordered_response") {
    return normalizeOrderedResponseBlock(block);
  }
  return block as ContentBlock;
}

function optionalBlockData(
  block: Record<string, unknown>,
): { data: string } | Record<string, never> {
  return typeof block.data === "string" ? { data: block.data } : {};
}

function normalizeOptions(raw: unknown): { id: string; text: string }[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((o) => o && typeof o === "object")
    .map((o) => {
      const item = o as Record<string, unknown>;
      return {
        id: String(item.id ?? "").trim(),
        text: String(item.text ?? "").trim(),
      };
    })
    .filter((o) => o.id && o.text);
}

function normalizeFillInBlankBlock(
  block: Record<string, unknown>,
): ContentBlock {
  const blanks = Array.isArray(block.blanks)
    ? block.blanks
        .filter((b) => b && typeof b === "object")
        .map((b) => {
          const item = b as Record<string, unknown>;
          return {
            id: String(item.id ?? "").trim(),
            ...(typeof item.label === "string" ? { label: item.label } : {}),
          };
        })
        .filter((b) => b.id)
    : [];
  const state = block.state === "disable" ? "disable" : "default";
  const userAnswers =
    block.user_answers && typeof block.user_answers === "object"
      ? (block.user_answers as Record<string, string>)
      : undefined;
  const answers =
    block.answers && typeof block.answers === "object" && !Array.isArray(block.answers)
      ? (block.answers as Record<string, string>)
      : undefined;
  return {
    type: "fill_in_blank",
    ...optionalBlockData(block),
    title: typeof block.title === "string" ? block.title : undefined,
    instruction:
      typeof block.instruction === "string" ? block.instruction : undefined,
    template: String(block.template ?? ""),
    blanks,
    options: normalizeOptions(block.options),
    state,
    ...(typeof block.is_correct === "boolean"
      ? { is_correct: block.is_correct }
      : {}),
    ...(userAnswers ? { user_answers: userAnswers } : {}),
    ...(answers ? { answers } : {}),
    ...(typeof block.feedback === "string" ? { feedback: block.feedback } : {}),
  };
}

function normalizeOrderingBlock(block: Record<string, unknown>): ContentBlock {
  const slotCount =
    typeof block.slot_count === "number"
      ? block.slot_count
      : Number(block.slot_count) || 0;
  const state = block.state === "disable" ? "disable" : "default";
  const userOrder = Array.isArray(block.user_order)
    ? block.user_order.map(String)
    : undefined;
  const answers = Array.isArray(block.answers)
    ? block.answers.map(String)
    : undefined;

  return {
    type: "ordering",
    ...optionalBlockData(block),
    title: typeof block.title === "string" ? block.title : undefined,
    instruction:
      typeof block.instruction === "string" ? block.instruction : undefined,
    prompt: typeof block.prompt === "string" ? block.prompt : undefined,
    slot_count: slotCount,
    options: normalizeOptions(block.options),
    state,
    ...(typeof block.is_correct === "boolean"
      ? { is_correct: block.is_correct }
      : {}),
    ...(userOrder ? { user_order: userOrder } : {}),
    ...(answers ? { answers } : {}),
    ...(typeof block.feedback === "string" ? { feedback: block.feedback } : {}),
  };
}

function normalizeOrderedResponseBlock(
  block: Record<string, unknown>,
): ContentBlock {
  const slotCount =
    typeof block.slot_count === "number"
      ? block.slot_count
      : Number(block.slot_count) || 0;
  const state = block.state === "disable" ? "disable" : "default";
  const userOrder = Array.isArray(block.user_order)
    ? block.user_order.map(String)
    : undefined;
  const answers = Array.isArray(block.answers)
    ? block.answers.map(String)
    : undefined;

  return {
    type: "ordered_response",
    ...optionalBlockData(block),
    title: typeof block.title === "string" ? block.title : undefined,
    instruction:
      typeof block.instruction === "string" ? block.instruction : undefined,
    prompt: typeof block.prompt === "string" ? block.prompt : undefined,
    slot_count: slotCount,
    options: normalizeOptions(block.options),
    state,
    ...(typeof block.is_correct === "boolean"
      ? { is_correct: block.is_correct }
      : {}),
    ...(userOrder ? { user_order: userOrder } : {}),
    ...(answers ? { answers } : {}),
    ...(typeof block.feedback === "string" ? { feedback: block.feedback } : {}),
  };
}

function normalizeMatchingBlock(block: Record<string, unknown>): ContentBlock {
  const normalizePairs = (raw: unknown) => {
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((p) => p && typeof p === "object")
      .map((p) => {
        const item = p as Record<string, unknown>;
        return {
          left_id: String(item.left_id ?? ""),
          right_id: String(item.right_id ?? ""),
          ...(typeof item.correct === "boolean"
            ? { correct: item.correct }
            : {}),
        };
      })
      .filter((p) => p.left_id && p.right_id);
  };
  const normalizeAnswersMap = (raw: unknown): Record<string, string> => {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
    return Object.entries(raw as Record<string, unknown>).reduce<
      Record<string, string>
    >((acc, [leftId, rightId]) => {
      const left = String(leftId ?? "").trim();
      const right = String(rightId ?? "").trim();
      if (left && right) acc[left] = right;
      return acc;
    }, {});
  };
  const state = block.state === "disable" ? "disable" : "default";
  const answers = normalizeAnswersMap(block.answers);
  const userPairs = block.user_pairs ? normalizePairs(block.user_pairs) : undefined;

  return {
    type: "matching",
    ...optionalBlockData(block),
    title: typeof block.title === "string" ? block.title : undefined,
    instruction:
      typeof block.instruction === "string" ? block.instruction : undefined,
    left: normalizeOptions(block.left),
    right: normalizeOptions(block.right),
    state,
    ...(typeof block.is_correct === "boolean"
      ? { is_correct: block.is_correct }
      : {}),
    ...(userPairs ? { user_pairs: userPairs } : {}),
    ...(Object.keys(answers).length ? { answers } : {}),
    ...(typeof block.feedback === "string" ? { feedback: block.feedback } : {}),
  };
}

function normalizeQuizTabStatus(rawStatus: unknown): QuizTabStatus | undefined {
  if (typeof rawStatus === "boolean") {
    return rawStatus ? "correct" : "incorrect";
  }

  if (typeof rawStatus === "string") {
    const normalized = rawStatus.trim().toLowerCase().replace(/[\s_-]+/g, "");
    if (!normalized) return undefined;
    if (
      normalized === "new" ||
      normalized === "pending" ||
      normalized === "unanswered" ||
      normalized === "notanswered"
    ) {
      return "new";
    }
    if (
      normalized === "correct" ||
      normalized === "right" ||
      normalized === "success" ||
      normalized === "passed" ||
      normalized === "true"
    ) {
      return "correct";
    }
    if (
      normalized === "incorrect" ||
      normalized === "wrong" ||
      normalized === "failed" ||
      normalized === "false" ||
      normalized === "notcorrect"
    ) {
      return "incorrect";
    }
  }

  return undefined;
}

function extractQuizTabStatus(raw: Record<string, unknown>): QuizTabStatus | undefined {
  const candidates: unknown[] = [
    raw.quiz_status,
    raw.quizStatus,
    raw.answer_status,
    raw.answerStatus,
    raw.answer_is_correct,
    raw.is_answer_correct,
    raw.is_correct,
    raw.isCorrect,
    raw.result,
    raw.status,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeQuizTabStatus(candidate);
    if (normalized) return normalized;
  }
  return undefined;
}

export function normalizeTab(
  raw: Record<string, unknown>,
): TabbedMessageContent {
  const rawId = raw.tab_id ?? raw.tabId ?? raw.id;
  const tabId =
    rawId === undefined || rawId === null ? "" : String(rawId).trim();
  const tabLabel = (raw.tab_label ?? raw.tabLabel ?? "") as string;
  const quizStatus = tabLabel.toLowerCase().includes("quiz")
    ? extractQuizTabStatus(raw)
    : undefined;

  const blocks = toBlockArray(raw.content_blocks);
  if (blocks.length > 0) {
    const resolvedTranscript = resolveTranscriptData({
      transcription: raw.transcription,
      transcriptionTimed: raw.transcription_timed,
    });
    const tabTranscriptionTimed: TabbedMessageContent["transcription_timed"] =
      resolvedTranscript.transcriptionTimed;
    const tabTranscription = resolvedTranscript.transcription;
    const base: TabbedMessageContent = {
      tab_id: tabId,
      tab_label: tabLabel,
      content_blocks: blocks.map((b) =>
        normalizeBlock(b as Record<string, unknown>),
      ),
      ...(quizStatus !== undefined ? { quiz_status: quizStatus } : {}),
      ...(tabTranscriptionTimed !== undefined
        ? {
            transcription_timed:
              tabTranscriptionTimed as TabbedMessageContent["transcription_timed"],
          }
        : {}),
    };
    return tabTranscription !== undefined
      ? { ...base, transcription: tabTranscription }
      : base;
  }

  const content = raw.content;
  if (content != null && typeof content === "string") {
    if (raw.message_type === "feedback") {
      const score = typeof raw.score === "number" ? raw.score : undefined;
      return {
        tab_id: tabId,
        tab_label: tabLabel,
        ...(quizStatus !== undefined ? { quiz_status: quizStatus } : {}),
        content_blocks: [
          { type: "feedback", text: content, score: score ?? 0 },
        ],
      };
    }
    return {
      tab_id: tabId,
      tab_label: tabLabel,
      ...(quizStatus !== undefined ? { quiz_status: quizStatus } : {}),
      content_blocks: [{ type: "text", content }],
    };
  }

  if (raw.type === "audio" && (raw.audio_url || raw.file_url)) {
    const audio_url = (raw.audio_url ?? raw.file_url ?? "") as string;
    const title = typeof raw.title === "string" ? raw.title : undefined;
    const resolvedTranscript = resolveTranscriptData({
      transcription: raw.transcription,
      transcriptionTimed: raw.transcription_timed,
    });
    const transcription_timed: Extract<
      ContentBlock,
      { type: "audio" }
    >["transcription_timed"] = resolvedTranscript.transcriptionTimed;
    const transcription = resolvedTranscript.transcription;
    const block =
      transcription !== undefined || transcription_timed !== undefined
        ? {
            type: "audio" as const,
            audio_url,
            ...(title !== undefined ? { title } : {}),
            ...(transcription !== undefined ? { transcription } : {}),
            ...(transcription_timed !== undefined
              ? {
                  transcription_timed: transcription_timed as Extract<
                    ContentBlock,
                    { type: "audio" }
                  >["transcription_timed"],
                }
              : {}),
          }
        : title !== undefined
          ? { type: "audio" as const, audio_url, title }
          : { type: "audio" as const, audio_url };
    return {
      tab_id: tabId,
      tab_label: tabLabel,
      ...(quizStatus !== undefined ? { quiz_status: quizStatus } : {}),
      content_blocks: [block],
      ...(transcription_timed !== undefined
        ? {
            transcription_timed:
              transcription_timed as TabbedMessageContent["transcription_timed"],
          }
        : {}),
    };
  }
  if (raw.type === "flash_card_image_collection" && raw.image_collection) {
    return {
      tab_id: tabId,
      tab_label: tabLabel,
      ...(quizStatus !== undefined ? { quiz_status: quizStatus } : {}),
      content_blocks: [
        {
          type: "flash_card_image_collection",
          image_collection: raw.image_collection as {
            url: string;
            description: string;
          }[],
        },
      ],
    };
  }
  if (raw.type === "flash_card" && raw.flash_card) {
    return {
      tab_id: tabId,
      tab_label: tabLabel,
      ...(quizStatus !== undefined ? { quiz_status: quizStatus } : {}),
      content_blocks: [
        { type: "flash_card", flash_card: normalizeFlashCard(raw.flash_card) },
      ],
    };
  }
  return {
    tab_id: tabId,
    tab_label: tabLabel,
    ...(quizStatus !== undefined ? { quiz_status: quizStatus } : {}),
    content: "",
  };
}

export function normalizeTabLabel(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

// UI tab id derived from duplicate backend ids, e.g. quiz1__2
export function isDerivedTabId(baseId: string, candidateId: string): boolean {
  if (!baseId || !candidateId) return false;
  if (candidateId === baseId) return true;
  return candidateId.startsWith(`${baseId}__`);
}

export function tabsShareBaseId(baseId: string, candidateId: string): boolean {
  if (!baseId || !candidateId) return false;
  return isDerivedTabId(baseId, candidateId) || isDerivedTabId(candidateId, baseId);
}

export function countTabsWithBaseId(
  tabs: TabbedMessageContent[],
  baseId: string,
): number {
  if (!baseId) return 0;
  return tabs.filter((tab) =>
    isDerivedTabId(baseId, tab.tab_id?.trim() ?? ""),
  ).length;
}

// Strip frontend disambiguation suffix before sending to backend.
export function toBackendTabId(tabId: string): string {
  return tabId.replace(/__\d+$/, "");
}

// Legacy prod messages may reuse tab_id. Assign stable suffixes by array order
export function ensureUniqueTabIds(
  tabs: TabbedMessageContent[],
): TabbedMessageContent[] {
  const seenCounts = new Map<string, number>();

  return tabs.map((tab, index) => {
    const baseId = tab.tab_id?.trim() || `tab_${index}`;
    const count = seenCounts.get(baseId) ?? 0;
    seenCounts.set(baseId, count + 1);

    if (count === 0) {
      return tab;
    }

    return {
      ...tab,
      tab_id: `${baseId}__${count + 1}`,
    };
  });
}

export function normalizeTabbedContent(raw: unknown): TabbedMessageContent[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return ensureUniqueTabIds(
    raw
      .filter(
        (t): t is Record<string, unknown> =>
          t !== null && typeof t === "object",
      )
      .map((t) => {
        const tab = normalizeTab(t);
        return t.csu_app_forward === true
          ? { ...tab, csu_app_forward: true }
          : tab;
      }),
  );
}

export function resolveTabIndexByUiId(
  tabs: TabbedMessageContent[],
  uiTabId: string,
): number {
  if (!tabs.length || !uiTabId) return -1;

  const exact = tabs.findIndex((tab) => tab.tab_id === uiTabId);
  if (exact >= 0) return exact;

  const dedupedIds = ensureUniqueTabIds(
    tabs.map((tab, index) => ({
      ...tab,
      tab_id: tab.tab_id?.trim() || `tab_${index}`,
    })),
  ).map((tab) => tab.tab_id);

  return dedupedIds.findIndex((id) => id === uiTabId);
}

export function findTabIndexForUpdate(
  tabs: TabbedMessageContent[],
  updatedTab: TabbedMessageContent,
): number {
  const updatedId = updatedTab.tab_id?.trim() ?? "";
  const updatedLabel = normalizeTabLabel(updatedTab.tab_label);

  if (updatedLabel) {
    const labelMatches = tabs
      .map((tab, index) => ({ tab, index }))
      .filter(({ tab }) => {
        const label = normalizeTabLabel(tab.tab_label);
        if (label !== updatedLabel) return false;
        if (!updatedId) return true;
        return isDerivedTabId(updatedId, tab.tab_id);
      });

    if (labelMatches.length === 1) {
      return labelMatches[0].index;
    }
  }

  const exactIdMatches = tabs
    .map((tab, index) => ({ tab, index }))
    .filter(({ tab }) => tab.tab_id === updatedId);
  if (exactIdMatches.length === 1) {
    return exactIdMatches[0].index;
  }
  if (exactIdMatches.length > 1 && updatedLabel) {
    const labelMatch = exactIdMatches.find(
      ({ tab }) => normalizeTabLabel(tab.tab_label) === updatedLabel,
    );
    if (labelMatch) return labelMatch.index;
  }
  if (exactIdMatches.length > 0) {
    return exactIdMatches[0].index;
  }

  const deduped = ensureUniqueTabIds(
    tabs.map((tab, index) => ({
      ...tab,
      tab_id: tab.tab_id?.trim() || `tab_${index}`,
    })),
  );
  const dedupedIndex = deduped.findIndex((tab) => tab.tab_id === updatedId);
  if (dedupedIndex >= 0) return dedupedIndex;

  if (updatedLabel) {
    return deduped.findIndex(
      (tab) => normalizeTabLabel(tab.tab_label) === updatedLabel,
    );
  }

  return -1;
}

export function normalizeContentBlock(raw: unknown): ContentBlock | null {
  if (!raw || typeof raw !== "object") return null;
  return normalizeBlock(raw as Record<string, unknown>);
}
